import { useCreateProduct } from '@/features/menu/hooks/useMenu';
import { useProductCodeConflict } from '@/features/menu/hooks/useProductCodeConflict';
import { compactLocalized } from '@/utils/localized';
import { fail, router } from '@/features/menu/utils/product-page-actions';
import { useT } from '@/i18n';
import { inputToMinor } from '@/utils/money';
import { normalizeProductCode, PRODUCT_CODE_MAX_LENGTH } from '@restaurant/types';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useState } from 'react';
import { Block, BlockFooter, BlockTitle, Button, f7, List, ListInput } from 'framework7-react';

// ─── Create form ─────────────────────────────────────────────────────────────

export default function CreateProductForm({
  menuId,
  categoryId,
}: {
  menuId: string;
  categoryId: string | undefined;
}) {
  const t = useT();
  const createProduct = useCreateProduct();
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const [name, setName] = useState<Record<string, string>>({});
  const [code, setCode] = useState('');
  const { conflictWith } = useProductCodeConflict(menuId);
  const codeConflict = conflictWith(code);
  const [price, setPrice] = useState('0');

  const create = () => {
    if (!name.en?.trim()) return f7.dialog.alert(t.menu.englishNameRequired);
    if (codeConflict) return;
    if (!categoryId) return f7.dialog.alert(t.menu.missingCategory);
    createProduct.mutate(
      {
        categoryId,
        body: {
          name: compactLocalized(name)!,
          code: normalizeProductCode(code),
          price: inputToMinor(price, currency),
        },
      },
      {
        onSuccess: (created) => {
          f7.toast.show({ text: t.menu.productCreated, closeTimeout: 2000 });
          router()?.navigate(`/menu/${menuId}/product/${created.id}`, {
            reloadCurrent: true,
          });
        },
        onError: fail,
      },
    );
  };

  return (
    <>
      <BlockTitle>{t.menu.newProduct}</BlockTitle>
      <List strongIos insetIos dividersIos>
        <ListInput
          label={t.menu.productForm.code}
          type="text"
          maxlength={PRODUCT_CODE_MAX_LENGTH}
          placeholder={t.menu.productForm.codePlaceholder}
          value={code}
          onInput={(e) => setCode((e.target as HTMLInputElement).value)}
          errorMessage={codeConflict ? t.menu.productForm.codeTakenBy(codeConflict) : undefined}
          errorMessageForce={!!codeConflict}
        />
        <ListInput
          label={t.menu.productForm.name}
          type="text"
          required
          validate
          value={name.en ?? ''}
          onInput={(e) => setName({ ...name, en: (e.target as HTMLInputElement).value })}
          errorMessage={t.menu.nameRequired}
          errorMessageForce={!name.en?.trim()}
        />
        <ListInput
          label={t.menu.basePrice}
          type="number"
          inputmode="decimal"
          value={price}
          onInput={(e) => setPrice((e.target as HTMLInputElement).value)}
        />
      </List>
      <Block>
        <Button
          fill
          round
          large
          onClick={create}
          disabled={createProduct.isPending || !!codeConflict}
        >
          {t.menu.createProduct}
        </Button>
      </Block>
      <BlockFooter>{t.menu.createProductNoteFooter}</BlockFooter>
    </>
  );
}
